import { autorun } from 'mobx';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useStore, useUser } from '@feathr/extender/state';

interface IUsePageTitleProps {
  pageTitle?: string;
  title?: ReactNode;
}

function getBackendEnvEmoji({
  isOnProduction,
  isOnStaging,
  isOnDevelopment,
}: {
  isOnProduction: boolean;
  isOnStaging: boolean;
  isOnDevelopment: boolean;
}): string {
  if (isOnProduction) {
    return '🚨 ';
  }
  if (isOnStaging) {
    return '🚧 ';
  }
  if (isOnDevelopment) {
    return '🛠️ ';
  }
  return '';
}

function usePageTitle({ pageTitle, title }: IUsePageTitleProps): void {
  const { eventId } = useParams<{ eventId: string }>();
  const { Events } = useStore();
  const user = useUser({ required: false });
  const { isOnProduction, isOnStaging, isOnDevelopment } = useBlackboxVersion();
  const titleString = pageTitle || (title && typeof title === 'string' ? title : '');

  // Sudoers can quickly tell which backend (dev/staging/prod) a tab is hitting at a glance.
  const titlePrefix = user?.isSudoer
    ? getBackendEnvEmoji({ isOnProduction, isOnStaging, isOnDevelopment })
    : '';

  useEffect(
    () =>
      autorun(() => {
        const titleSuffix = eventId ? Events.get(eventId)?.name : 'Feathr';
        // Bail early if event name is undefined - autorun will rerun once the name resolves
        if (titleSuffix === undefined) {
          return;
        }
        const base = titleString ? `${titleString} | ${titleSuffix}` : titleSuffix;
        document.title = `${titlePrefix}${base}`;
      }),
    [eventId, titleString, titlePrefix],
  );
}

export default usePageTitle;
